import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sparkles, FolderPlus, Folder, ChevronRight, Loader2, ArrowLeft, ArrowRight, Check, X, Search,
} from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROOT_KEY = '__root__';

/**
 * Multi-target template applier with a 3-step flow:
 *   1. Pick template
 *   2. Pick target locations (existing folders ✓ checkboxes + "Apply at space root" + inline "Create new folder")
 *   3. Preview what will be created where → Confirm
 *
 * The API only accepts one `target_parent_folder_id` per call, so on Confirm
 * we fan out one /apply request per selected target in parallel.
 */
export function ApplyTemplateDialog({
  open, onOpenChange, wsId, activeSpace, currentFolderId,
  spaceName, folders = [], onApplied,
}) {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  // Set of target folder ids; ROOT_KEY means "apply at space root"
  const [selectedTargets, setSelectedTargets] = useState(new Set());
  // Names of new folders to create at root before applying template inside them
  const [newFolders, setNewFolders] = useState([]);
  const [newFolderDraft, setNewFolderDraft] = useState('');
  const [search, setSearch] = useState('');
  const [applying, setApplying] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedTemplateId('');
      setTemplateSearch('');
      setSelectedTargets(currentFolderId ? new Set([currentFolderId]) : new Set([ROOT_KEY]));
      setNewFolders([]);
      setNewFolderDraft('');
      setSearch('');
      loadTemplates();
    }
  }, [open]); // eslint-disable-line

  const loadTemplates = async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/folder-templates?workspace_id=${wsId}&include_presets=true`);
      if (r.ok) setTemplates(await r.json());
    } catch { /* silent */ }
  };

  const selectedTemplate = useMemo(
    () => templates.find(t => t.template_id === selectedTemplateId),
    [templates, selectedTemplateId],
  );

  // Filter templates by name / description / tags
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => {
      const hay = [
        t.name,
        t.description,
        ...(t.tags || []),
        t.is_system ? 'system' : 'custom',
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [templates, templateSearch]);

  const isClientOrVendorRoot = (activeSpace === 'client' || activeSpace === 'vendor');

  // Eligible existing folders for the picker. At client/vendor root, only show
  // contact-root folders to avoid the "ghost folder" issue.
  const pickerFolders = useMemo(() => {
    let list = folders.filter(f => !f.is_deleted && !f.is_archived);
    if (isClientOrVendorRoot && !currentFolderId) {
      list = list.filter(f => f.is_contact_root);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(f => (f.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [folders, search, isClientOrVendorRoot, currentFolderId]);

  const toggleTarget = (key) => {
    setSelectedTargets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const addDraftFolder = () => {
    const name = newFolderDraft.trim();
    if (!name) return;
    // Block at client/vendor root — same rule as the main folder create.
    if (isClientOrVendorRoot && !currentFolderId) {
      toast.error('Customer / vendor folders are created automatically when you add a contact. Pick an existing customer folder instead.');
      return;
    }
    if (newFolders.includes(name)) { setNewFolderDraft(''); return; }
    setNewFolders([...newFolders, name]);
    setNewFolderDraft('');
  };

  const totalTargets = selectedTargets.size + newFolders.length;
  const canNext = selectedTemplateId && totalTargets > 0;

  const handleApply = async () => {
    if (!selectedTemplate) return;
    setApplying(true);
    try {
      // Step A: create new folders, capture their ids
      const createdFolderIds = [];
      for (const name of newFolders) {
        const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            parent_folder_id: currentFolderId,
            space: activeSpace,
          }),
        });
        if (r.ok) {
          const f = await r.json();
          createdFolderIds.push(f.folder_id);
        } else {
          const e = await r.json().catch(() => ({}));
          toast.error(`Couldn't create "${name}": ${e.detail || 'unknown error'}`);
        }
      }

      // Step B: fan out apply calls — one per selected target
      const targets = [...selectedTargets].map(k => (k === ROOT_KEY ? null : k));
      const allTargets = [...targets, ...createdFolderIds];
      const results = await Promise.all(allTargets.map(t =>
        apiFetch(`${API_URL}/api/folder-templates/${selectedTemplateId}/apply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_id: wsId,
            target_parent_folder_id: t,
            space: activeSpace,
          }),
        }).then(async r => ({ ok: r.ok, body: await r.json().catch(() => ({})), target: t })),
      ));

      const succeeded = results.filter(r => r.ok).length;
      const totalCreated = results.reduce((s, r) => s + (r.body?.created_count || 0), 0);
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        toast.success(`Created ${totalCreated} folder${totalCreated === 1 ? '' : 's'} across ${succeeded} location${succeeded === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
        onApplied?.();
        onOpenChange(false);
      } else {
        const firstErr = results[0]?.body?.detail || 'Failed to apply template';
        toast.error(firstErr);
      }
    } catch (e) {
      toast.error('Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  // Display labels for selected targets (used in preview step)
  const targetLabels = useMemo(() => {
    const labels = [];
    selectedTargets.forEach(k => {
      if (k === ROOT_KEY) {
        labels.push({ key: k, label: `${spaceName || 'Space'} root`, isRoot: true });
      } else {
        const f = folders.find(x => x.folder_id === k);
        labels.push({ key: k, label: f?.name || 'Unknown folder', isRoot: false });
      }
    });
    newFolders.forEach(n => labels.push({ key: `new:${n}`, label: n, isNew: true }));
    return labels;
  }, [selectedTargets, newFolders, folders, spaceName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        data-testid="apply-template-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Apply folder template
          </DialogTitle>
          <DialogDescription>
            Step {step} of 3 — {step === 1 ? 'pick a template' : step === 2 ? 'choose where to apply it' : 'review and confirm'}
          </DialogDescription>
        </DialogHeader>

        {/* Step progress dots */}
        <div className="flex items-center gap-2 px-1">
          {[1, 2, 3].map(s => (
            <div key={s} className={cn(
              'h-1.5 flex-1 rounded-full transition-all',
              s <= step ? 'bg-primary' : 'bg-muted',
            )} />
          ))}
        </div>

        {/* Step 1 — Template picker */}
        {step === 1 && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search templates by name, description, or tag…"
                className="pl-9 h-9"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                data-testid="apply-template-search"
                autoFocus
              />
            </div>
            <ScrollArea className="flex-1 -mx-2 px-2">
              <div className="space-y-2 py-2">
                {templates.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No templates available.</p>
                ) : filteredTemplates.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center italic">
                    No templates match &ldquo;{templateSearch}&rdquo;.
                  </p>
                ) : filteredTemplates.map(t => (
                  <button
                    type="button"
                    key={t.template_id}
                    onClick={() => setSelectedTemplateId(t.template_id)}
                    className={cn(
                      'w-full text-left p-3 rounded-lg border transition-all flex items-start gap-3',
                      selectedTemplateId === t.template_id
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'hover:border-primary/40 hover:bg-muted/50',
                    )}
                    data-testid={`template-option-${t.template_id}`}
                  >
                    <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Folder className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{t.name}</span>
                        {t.is_system && <Badge variant="secondary" className="text-[10px] h-4 px-1.5">System</Badge>}
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                          {t.node_count || t.nodes?.length || 0} folders
                        </Badge>
                        {(t.tags || []).slice(0, 2).map(tag => (
                          <Badge key={tag} variant="outline" className="text-[10px] h-4 px-1.5 bg-muted/40">
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                      {t.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>}
                    </div>
                    {selectedTemplateId === t.template_id && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </ScrollArea>
            {templateSearch && filteredTemplates.length > 0 && filteredTemplates.length < templates.length && (
              <p className="text-[11px] text-muted-foreground text-center mt-1">
                Showing {filteredTemplates.length} of {templates.length} templates
              </p>
            )}
          </div>
        )}

        {/* Step 2 — Target picker */}
        {step === 2 && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search folders…"
                  className="pl-9 h-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="apply-template-folder-search"
                />
              </div>

              {/* Apply at root option */}
              {!currentFolderId && !isClientOrVendorRoot && (
                <button
                  type="button"
                  onClick={() => toggleTarget(ROOT_KEY)}
                  className={cn(
                    'w-full text-left p-2.5 rounded-lg border flex items-center gap-3 transition-all',
                    selectedTargets.has(ROOT_KEY)
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/50',
                  )}
                  data-testid="apply-target-root"
                >
                  <Checkbox checked={selectedTargets.has(ROOT_KEY)} onCheckedChange={() => toggleTarget(ROOT_KEY)} />
                  <div className="w-7 h-7 rounded bg-muted flex items-center justify-center">
                    <Folder className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{spaceName || 'Space'} root</p>
                    <p className="text-[11px] text-muted-foreground">Apply template at the top level of this space</p>
                  </div>
                </button>
              )}

              {/* Inline new folder creator */}
              {!isClientOrVendorRoot && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <FolderPlus className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Or create a new folder to apply into…"
                        value={newFolderDraft}
                        onChange={(e) => setNewFolderDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDraftFolder())}
                        className="pl-9 h-9"
                        data-testid="apply-template-new-folder-input"
                      />
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={addDraftFolder} disabled={!newFolderDraft.trim()} data-testid="apply-template-new-folder-add">
                      Add
                    </Button>
                  </div>
                  {newFolders.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {newFolders.map(n => (
                        <Badge key={n} variant="secondary" className="gap-1 pr-1" data-testid={`draft-folder-${n}`}>
                          <FolderPlus className="w-3 h-3" />
                          {n}
                          <button
                            type="button"
                            onClick={() => setNewFolders(newFolders.filter(x => x !== n))}
                            className="hover:bg-muted rounded p-0.5"
                            aria-label={`Remove ${n}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Existing folders multi-select */}
            <ScrollArea className="flex-1 -mx-2 px-2 mt-3">
              <div className="space-y-1 py-1">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider px-1 mb-1">
                  Existing folders ({pickerFolders.length})
                </p>
                {pickerFolders.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center italic">
                    {search ? 'No matches' : 'No folders yet — use "Create new" above or apply at root.'}
                  </p>
                ) : pickerFolders.map(f => (
                  <button
                    type="button"
                    key={f.folder_id}
                    onClick={() => toggleTarget(f.folder_id)}
                    className={cn(
                      'w-full text-left p-2 rounded flex items-center gap-3 transition-all',
                      selectedTargets.has(f.folder_id)
                        ? 'bg-primary/5'
                        : 'hover:bg-muted/50',
                    )}
                    data-testid={`apply-target-folder-${f.folder_id}`}
                  >
                    <Checkbox checked={selectedTargets.has(f.folder_id)} onCheckedChange={() => toggleTarget(f.folder_id)} />
                    <Folder className="w-3.5 h-3.5 flex-shrink-0" style={{ color: f.color || undefined }} />
                    <span className="text-sm truncate flex-1">{f.name}</span>
                    {f.is_contact_root && <Badge variant="outline" className="text-[10px] h-4 px-1.5">Contact</Badge>}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Step 3 — Preview / Confirm */}
        {step === 3 && selectedTemplate && (
          <ScrollArea className="flex-1 -mx-2 px-2">
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Template</p>
                <p className="font-medium text-sm mt-0.5">{selectedTemplate.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Creates {selectedTemplate.node_count || selectedTemplate.nodes?.length || 0} folder
                  {(selectedTemplate.node_count || selectedTemplate.nodes?.length || 0) === 1 ? '' : 's'}
                </p>
              </div>

              <div>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                  Applies to {targetLabels.length} location{targetLabels.length === 1 ? '' : 's'}
                </p>
                <div className="space-y-1.5">
                  {targetLabels.map(t => (
                    <div
                      key={t.key}
                      className="flex items-center gap-2 p-2 rounded border bg-card"
                      data-testid={`preview-target-${t.key}`}
                    >
                      {t.isNew ? (
                        <FolderPlus className="w-4 h-4 text-emerald-500" />
                      ) : t.isRoot ? (
                        <Folder className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Folder className="w-4 h-4 text-primary" />
                      )}
                      <span className="text-sm flex-1">{t.label}</span>
                      {t.isNew && <Badge variant="outline" className="text-[10px] h-4 px-1.5">Will create</Badge>}
                      <ChevronRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        +{selectedTemplate.node_count || selectedTemplate.nodes?.length || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-muted-foreground italic px-1">
                Total: {((selectedTemplate.node_count || selectedTemplate.nodes?.length || 0) * targetLabels.length)} folders will be created.
              </p>
            </div>
          </ScrollArea>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t pt-3 mt-2">
          <div className="text-xs text-muted-foreground">
            {step === 1 && (selectedTemplateId ? '1 template selected' : 'Select a template to continue')}
            {step === 2 && `${totalTargets} location${totalTargets === 1 ? '' : 's'} selected`}
            {step === 3 && 'Review then confirm'}
          </div>
          <div className="flex gap-2">
            {step > 1 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep(step - 1)} disabled={applying} data-testid="apply-template-back">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            )}
            {step < 3 ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !selectedTemplateId : !canNext}
                data-testid="apply-template-next"
              >
                Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={handleApply}
                disabled={applying}
                data-testid="apply-template-confirm-final"
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Apply template
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
